import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import DatePicker from '../DatePicker';
import Select from '../Select';
import SearchableSelect from '../SearchableSelect';
import { groupDigits, handleAmountInput } from '../../utils/money';
import AttachmentPicker from './AttachmentPicker';
import {
  DepartmentPills, formatCurrency, formatDate, todayISO, errorText, inputClass, labelClass,
} from './shared';

// Money drawn from the bank into the cash box: a cheque cashed, a transfer received, or cash
// taken at the counter. Filed against a department, because that is whose cash it becomes. A
// cheque is withdrawn once; the server refuses a second withdrawal on the same number and bank
// (CHEQUE_ALREADY_WITHDRAWN) and the refusal is shown here with the earlier one.

const METHODS = [
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'CASH', label: 'Cash from the counter' },
];

const WithdrawalModal = ({ editing, cats, initialDepartment, meta, onClose, onSaved }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const isEditing = !!editing;

  const [form, setForm] = useState(() => {
    if (editing) {
      const isChild = editing.categoryId && editing.categoryId !== editing.rootCategoryId;
      return {
        entryDate: editing.entryDate || todayISO(),
        department: editing.rootCategoryId || '',
        categoryId: isChild ? editing.categoryId : '',
        amount: String(Math.round(Number(editing.amount || 0)) || ''),
        method: ['CHEQUE', 'BANK_TRANSFER', 'CASH'].includes(editing.paymentMode) ? editing.paymentMode : 'BANK_TRANSFER',
        chequeNumber: editing.chequeNumber || '',
        txnNumber: editing.txnNumber || '',
        chequeBank: editing.chequeBank || '',
        description: editing.description || '',
        notes: editing.notes || '',
      };
    }
    const department = initialDepartment && initialDepartment !== 'ALL' ? initialDepartment : '';
    return {
      entryDate: todayISO(), department, categoryId: '', amount: '', method: 'CHEQUE',
      chequeNumber: '', txnNumber: '', chequeBank: '', description: '', notes: '',
    };
  });
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState(editing?.attachments || []);
  const [clash, setClash] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const amountRef = useRef(null);
  const dirtyRef = useRef(false);
  const dialogRef = useDialog(onClose, { isDirty: () => dirtyRef.current });

  const set = useCallback((key, value) => {
    dirtyRef.current = true;
    setForm(f => ({ ...f, [key]: value }));
    setErrors(e => (e[key] ? { ...e, [key]: undefined } : e));
    setClash(null);
  }, []);

  const roots = cats.roots;
  const children = useMemo(() => cats.childrenOf(form.department), [cats, form.department]);
  const banks = meta?.banks || [];
  const isCheque = form.method === 'CHEQUE';
  const isTransfer = form.method === 'BANK_TRANSFER';

  useEffect(() => {
    if (!form.department && roots.length === 1) setForm(f => ({ ...f, department: roots[0].id }));
  }, [roots, form.department]);

  // The bank this department last drew from, as soon as the department is known.
  useEffect(() => {
    if (form.method === 'CASH' || form.chequeBank || !form.department) return;
    const last = meta?.bankByDepartment?.[form.department];
    if (last) setForm(f => (f.chequeBank ? f : { ...f, chequeBank: last }));
  }, [form.method, form.chequeBank, form.department, meta]);

  const validate = () => {
    const e = {};
    if (!form.department) e.department = 'Pick the department this cash belongs to.';
    if (!form.entryDate) e.entryDate = 'Pick the date.';
    if (!form.amount || Number(form.amount) <= 0) e.amount = 'Enter the amount.';
    if (isCheque && !form.chequeNumber.trim()) e.chequeNumber = 'Enter the cheque number.';
    if (isCheque && !form.chequeBank) e.chequeBank = 'Pick the bank.';
    if (isTransfer && !form.txnNumber.trim()) e.txnNumber = 'Enter the transfer reference.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (addAnother) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        entryDate: form.entryDate,
        description: form.description.trim(),
        amount: Number(form.amount),
        entryType: 'WITHDRAWAL',
        categoryId: form.categoryId || form.department,
        paymentMode: form.method,
        chequeNumber: isCheque ? form.chequeNumber.trim() : '',
        txnNumber: isTransfer ? form.txnNumber.trim() : '',
        chequeBank: form.method === 'CASH' ? '' : form.chequeBank,
        notes: form.notes.trim(),
        confirmDuplicate: true,
        recordWithdrawal: false,
      };
      const saved = isEditing
        ? await expenseAPI.updateEntry(editing.id, payload, { attachments: files })
        : await expenseAPI.createEntry(payload, { attachments: files });
      showToast('success', `${isEditing ? 'Updated' : 'Recorded'}: ${saved.description}, ${formatCurrency(saved.amount)}`);
      if (addAnother) {
        setSavedCount(n => n + 1);
        setForm(f => ({ ...f, amount: '', chequeNumber: '', txnNumber: '', description: '', notes: '' }));
        setFiles([]);
        setErrors({});
        dirtyRef.current = false;
        onSaved(saved, { keepOpen: true });
        amountRef.current?.focus();
      } else {
        dirtyRef.current = false;
        onSaved(saved, { keepOpen: false });
      }
    } catch (err) {
      if (err.status === 409 && err.code === 'CHEQUE_ALREADY_WITHDRAWN') {
        setClash({ message: err.message, existing: err.body?.existing });
      } else {
        showToast('error', errorText(err, 'Failed to record the withdrawal'));
      }
    } finally {
      setSaving(false);
    }
  };

  const removeExisting = async (a) => {
    const ok = await confirm({
      title: 'Remove this file?',
      message: `${a.originalFileName || 'The file'} will be removed from the entry.`,
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await expenseAPI.deleteAttachment(a.id);
      setExistingAttachments(list => list.filter(x => x.id !== a.id));
    } catch (err) {
      showToast('error', errorText(err, 'Could not remove the file'));
    }
  };

  const descriptionPlaceholder = isCheque
    ? `Optional, defaults to "Cheque ${form.chequeNumber.trim() || '…'}"`
    : isTransfer ? 'Optional, defaults to "Bank transfer …"' : 'Optional, e.g. September expenses';

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="withdrawal-title">
        <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-navy-800 to-navy-700 flex items-center justify-between">
          <div>
            <h2 id="withdrawal-title" className="text-lg font-semibold text-white">{isEditing ? 'Edit withdrawal' : 'Record a withdrawal'}</h2>
            <p className="text-xs text-navy-100 mt-0.5">
              {savedCount > 0 ? `${savedCount} recorded so far in this run` : 'Cash drawn from the bank into the cash box'}
            </p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-800">
                A withdrawal adds to the department's cash in hand. An expense paid straight by cheque or transfer
                does not need one entered here: the Add expense form records it alongside.
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Department <span className="text-red-500">*</span></label>
            <DepartmentPills roots={roots} value={form.department} onChange={(v) => { set('department', v); set('categoryId', ''); set('chequeBank', ''); }} required label="Department" />
            {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Category</label>
              <Select
                value={form.categoryId}
                onChange={(v) => set('categoryId', v)}
                placeholder={form.department ? 'Whole department' : 'Pick the department first'}
                disabled={!form.department}
                options={[{ value: '', label: 'Whole department' }, ...children.map(c => ({ value: c.id, label: c.name }))]}
                buttonClassName="px-4 py-2.5 rounded-xl"
              />
            </div>
            <div>
              <label className={labelClass}>Date <span className="text-red-500">*</span></label>
              <DatePicker value={form.entryDate} onChange={(v) => set('entryDate', v)} max={todayISO()} error={!!errors.entryDate} />
              {errors.entryDate && <p className="text-red-500 text-xs mt-1">{errors.entryDate}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="withdrawal-amount" className={labelClass}>Amount <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Rs</span>
                <input
                  id="withdrawal-amount"
                  ref={amountRef}
                  type="text"
                  inputMode="numeric"
                  value={groupDigits(form.amount)}
                  onChange={(e) => handleAmountInput(e, form.amount, (d) => set('amount', d))}
                  placeholder="0"
                  className={`${inputClass} pl-11 text-lg font-semibold ${errors.amount ? 'border-red-300' : ''}`}
                />
              </div>
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
            <div>
              <label className={labelClass}>How</label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="How">
                {METHODS.map(m => (
                  <button
                    key={m.value}
                    type="button"
                    aria-pressed={form.method === m.value}
                    onClick={() => set('method', m.value)}
                    className={`px-3.5 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                      form.method === m.value ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {form.method !== 'CASH' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isCheque ? (
                <div>
                  <label htmlFor="withdrawal-cheque" className={labelClass}>Cheque number <span className="text-red-500">*</span></label>
                  <input
                    id="withdrawal-cheque"
                    type="text"
                    value={form.chequeNumber}
                    onChange={(e) => set('chequeNumber', e.target.value)}
                    placeholder="e.g. 110641"
                    className={`${inputClass} font-mono ${errors.chequeNumber ? 'border-red-300' : ''}`}
                  />
                  {errors.chequeNumber && <p className="text-red-500 text-xs mt-1">{errors.chequeNumber}</p>}
                </div>
              ) : (
                <div>
                  <label htmlFor="withdrawal-txn" className={labelClass}>Transfer reference <span className="text-red-500">*</span></label>
                  <input
                    id="withdrawal-txn"
                    type="text"
                    value={form.txnNumber}
                    onChange={(e) => set('txnNumber', e.target.value)}
                    placeholder="e.g. TRX-88213445"
                    className={`${inputClass} font-mono ${errors.txnNumber ? 'border-red-300' : ''}`}
                  />
                  {errors.txnNumber && <p className="text-red-500 text-xs mt-1">{errors.txnNumber}</p>}
                </div>
              )}
              <div>
                <SearchableSelect
                  label={isCheque ? 'Bank *' : 'Bank'}
                  value={form.chequeBank}
                  onChange={(v) => set('chequeBank', v)}
                  options={banks}
                  placeholder="Bank"
                  allowCustom
                  error={errors.chequeBank}
                />
              </div>
            </div>
          )}

          <div>
            <label htmlFor="withdrawal-description" className={labelClass}>Description</label>
            <input
              id="withdrawal-description"
              type="text"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder={descriptionPlaceholder}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="withdrawal-notes" className={labelClass}>Notes</label>
            <textarea
              id="withdrawal-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className={`${inputClass} resize-none`}
            />
          </div>

          <AttachmentPicker
            label="Cheque photo or transfer slip"
            files={files}
            onFiles={(f) => { setFiles(f); dirtyRef.current = true; }}
            existing={existingAttachments}
            onRemoveExisting={isEditing ? removeExisting : undefined}
            fileUrl={expenseAPI.attachmentFileUrl}
          />

          {clash && (
            <div role="alert" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <p className="font-semibold">This cheque has already been withdrawn.</p>
                  {clash.existing && (
                    <p className="mt-0.5">
                      "{clash.existing.description}" for {formatCurrency(clash.existing.amount)} on {formatDate(clash.existing.entryDate)}
                      {clash.existing.categoryPath ? ` under ${clash.existing.categoryPath}` : ''}.
                    </p>
                  )}
                  <p className="mt-1 text-xs text-red-800">
                    A cheque is withdrawn once. Expenses paid out of it may carry its number, but a second withdrawal
                    would count the cash twice. Check the number, or edit the earlier withdrawal from the Cheques page.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3 flex-wrap">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Cancel
          </button>
          {!isEditing && (
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="px-5 py-2.5 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors font-medium disabled:opacity-50"
            >
              Save &amp; add another
            </button>
          )}
          <button
            type="button"
            onClick={() => save(false)}
            disabled={saving}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
            {isEditing ? 'Save changes' : 'Save withdrawal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalModal;
